import PageHero from "@/components/landing/PageHero";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import {
  Target,
  Eye,
  Heart,
  ShieldCheck,
  Microscope,
  Users2,
  Lightbulb,
  HandHeart,
  Scale,
  Sparkles,
  ArrowRight,
  Timer,
  FileCheck2,
  TrendingDown,
  CheckCircle2,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Customer Outcomes — lead with what changes for the user            */
/* ------------------------------------------------------------------ */

const outcomes = [
  {
    icon: Timer,
    metric: "Connected",
    label: "Quality operations",
    description:
      "Quality, laboratory and maintenance teams work from linked records instead of reconciling separate systems and spreadsheets.",
  },
  {
    icon: FileCheck2,
    metric: "Traceable",
    label: "Reviewed outputs",
    description:
      "Investigations, deviations, changes and reviews retain their sources, decisions, signatures and audit history.",
  },
  {
    icon: TrendingDown,
    metric: "One model",
    label: "Three operating domains",
    description:
      "Pharmalify Spine connects Quality (eQMS), QC Lab (LIMS) and Asset Integrity (CMMS) with connected records and traceable links between workflows.",
  },
  {
    icon: CheckCircle2,
    metric: "Human",
    label: "Decision authority",
    description:
      "AI proposes and cites; qualified people decide, approve and remain accountable for every regulated action.",
  },
];

/* ------------------------------------------------------------------ */
/*  Mission / Vision / Values                                          */
/* ------------------------------------------------------------------ */

const pillars = [
  {
    icon: Target,
    title: "Our Mission",
    body: [
      "To reduce the skilled time lost to disconnected records, repetitive review cycles and fragmented compliance tooling across the biopharma value chain.",
      "Pharmalify Spine unites Quality, QC Lab and Asset Integrity, with native AI Agents and Regulatory Watch — built for emerging biotech, growth biopharma, multi-site mid-size organizations and CDMO/CRO teams.",
    ],
  },
  {
    icon: Eye,
    title: "Our Vision",
    body: [
      "A biopharma landscape where every regulated organization — regardless of size — can operate on connected, validation-ready quality infrastructure that keeps pace with evolving requirements.",
      "A future where quality documentation is no longer a bottleneck but a strategic asset: continuously aligned with global standards, transparently auditable, and seamlessly woven into the daily work of cross-functional teams.",
    ],
  },
  {
    icon: Heart,
    title: "Our Values",
    body: [
      "Data integrity, regulatory rigor and patient safety are non-negotiable. AI remains decision support; qualified people verify and approve regulated work.",
      "We build for trust — not speed alone. Transparency, traceability and Human-in-the-Loop control are embedded in every workflow, every decision and every line of code we ship.",
    ],
  },
];

const principles = [
  {
    icon: ShieldCheck,
    title: "Integrity First",
    description:
      "GxP, ALCOA+ and 21 CFR Part 11 are starting points — not aspirations. Every workflow is engineered to preserve evidentiary value end-to-end.",
  },
  {
    icon: Users2,
    title: "Human-in-the-Loop",
    description:
      "AI accelerates research, comparison and drafting; qualified people verify the output and retain every GxP decision.",
  },
  {
    icon: Microscope,
    title: "Scientific Rigor",
    description:
      "Our agents reason over ICH Q7–Q12, 21 CFR Parts 210/211/820, EU Annex 11 & 15 and curated regulatory corpora — not generic web data.",
  },
  {
    icon: Scale,
    title: "Governance by Design",
    description:
      "Role-based access, full audit trails and organization-level data isolation are built into the foundation — not bolted on as features.",
  },
  {
    icon: Lightbulb,
    title: "Pragmatic Innovation",
    description:
      "We adopt new AI capabilities only when they demonstrably reduce risk, improve accuracy or free expert time for higher-value work.",
  },
  {
    icon: HandHeart,
    title: "Customer Partnership",
    description:
      "We grow with our customers — from individual quality leads to enterprise QA organizations — adapting the platform to real operational realities.",
  },
];

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

const About = () => {
  useSEO({
    title: "About Pharmalify | Connected Biopharma Quality Systems",
    description:
      "Pharmalify Spine unifies eQMS, LIMS and CMMS with native governed AI Agents and Regulatory Watch for small-to-mid biopharma.",
    keywords:
      "about Pharmalify, pharmaceutical quality software, validation-ready eQMS, biopharma compliance suite, governed AI",
    canonical: "https://www.pharmalify.com/about",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "About Pharmalify",
      url: "https://www.pharmalify.com/about",
      description:
         "Pharmalify Spine is a unified GxP operations backbone with eQMS, LIMS and CMMS, native AI Agents and Regulatory Watch.",
      about: {
        "@type": "Organization",
        name: "Pharmalify",
        url: "https://www.pharmalify.com",
      },
    },
  });
  return (
    <main className="min-h-screen bg-background">
      <PageHero
        title="About Pharmalify"
         description="Pharmalify Spine: a connected GxP operations backbone for startups and small-to-mid-sized regulated organizations, with the eQMS as its system of record."
      />

      {/* ======== Customer Outcomes (lead) ======== */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-accent mb-3 px-3 py-1 rounded-full bg-accent/10">
              What Changes for Your Team
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
             Built around the work regulated teams must control
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Pharmalify exists to remove the documentation tax on regulated work — so quality, regulatory, operations and supplier-management teams can spend their expertise where it matters most: on the product and the patient.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
            {outcomes.map((o) => (
              <Card key={o.label} className="border-border/60 hover:shadow-lg hover:border-accent/30 transition-all">
                <CardContent className="p-6 flex flex-col h-full">
                  <div className="w-11 h-11 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                    <o.icon className="w-5 h-5 text-accent" />
                  </div>
                  <div className="text-2xl font-bold text-foreground">{o.metric}</div>
                  <div className="text-sm font-semibold text-accent mb-3">{o.label}</div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {o.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-xs text-muted-foreground text-center mt-6 max-w-2xl mx-auto italic">
            Figures reflect typical outcomes observed across pilot deployments and design partners; actual results depend on scope, process maturity and adoption.
          </p>
        </div>
      </section>

      {/* ======== Who We Are ======== */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container max-w-4xl text-center">
          <span className="inline-block text-xs font-bold tracking-widest uppercase text-accent mb-3 px-3 py-1 rounded-full bg-accent/10">
            Who We Are
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
            A technology enabler built by and for biopharma
          </h2>
          <p className="text-muted-foreground leading-relaxed">
               Pharmalify sits at the intersection of life sciences, regulated quality management and applied artificial intelligence. We work with biotech innovators, pharmaceutical manufacturers, CDMOs and CROs to reduce the reconciliation and documentation burden created by disconnected systems. Pharmalify Spine connects Quality (eQMS), QC Lab (LIMS) and Asset Integrity (CMMS) in one governed model. Native AI Agents propose and cite, never decide; Regulatory Watch feeds Change Control, QRM and the SOP lifecycle.
          </p>
        </div>
      </section>

      {/* ======== Mission / Vision / Values ======== */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="text-center mb-12">
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-accent mb-3 px-3 py-1 rounded-full bg-accent/10">
              What Drives Us
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
              Mission, Vision & Values
            </h2>
          </div>
          <div className="grid lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {pillars.map((item) => (
              <Card
                key={item.title}
                className="border-border/60 hover:shadow-lg transition-shadow h-full"
              >
                <CardContent className="p-8 flex flex-col h-full">
                  <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center mb-5">
                    <item.icon className="w-7 h-7 text-accent" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-4">
                    {item.title}
                  </h3>
                  <div className="space-y-3 text-muted-foreground leading-relaxed text-[15px]">
                    {item.body.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ======== Guiding Principles ======== */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container">
          <div className="text-center mb-12">
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-accent mb-3 px-3 py-1 rounded-full bg-accent/10">
              How We Work
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
              Guiding Principles
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-base">
              The non-negotiables that shape every product decision, every workflow and every customer engagement.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
            {principles.map((p) => (
              <Card
                key={p.title}
                className="border-border/60 hover:shadow-lg hover:border-accent/30 transition-all h-full"
              >
                <CardContent className="p-6 flex flex-col h-full">
                  <div className="w-11 h-11 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                    <p.icon className="w-5 h-5 text-accent" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-2">
                    {p.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {p.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ======== Our Commitment ======== */}
      <section className="py-16 md:py-24">
        <div className="container max-w-4xl">
          <div className="text-center mb-10">
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-accent mb-3 px-3 py-1 rounded-full bg-accent/10">
              Our Commitment
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
              Technology that earns the trust of regulated industries
            </h2>
          </div>
          <div className="space-y-5 text-muted-foreground leading-relaxed">
            <p>
              The organizations we serve operate under intense regulatory scrutiny, where a single documentation gap can delay a batch release, trigger an audit finding or — most importantly — affect patient outcomes. That responsibility shapes how we build.
            </p>
            <p>
               We design for traceability, reproducibility and auditability, and AI assistance runs within permission-scoped controls. Each contracted organization receives an environment configured to its intended use, roles, sites and validation responsibilities.
            </p>
            <p className="font-medium text-foreground text-center">
              We aim to be a long-term partner to the people who safeguard the medicines patients depend on.
            </p>
          </div>
          <div className="mt-10 flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Sparkles className="w-4 h-4 text-accent" />
            <span>Built with rigor. Delivered with accountability.</span>
          </div>
        </div>
      </section>

      {/* ======== CTA ======== */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              Discuss your scope with our team
            </h2>
            <p className="text-muted-foreground leading-relaxed">
               Review Pharmalify Spine in detail, or tell us about your sites, users and intended use.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
               <Link to="/spine">
                <Button variant="clinical" size="lg">
                   Explore Pharmalify Spine
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" size="lg">
                   Request Walkthrough
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
